/**
 * Link Service - Frontend utilities for interview links
 * 
 * Note: Password hashing is done server-side (Supabase RPC or Cloudflare Worker).
 * This file only contains client-safe utilities.
 */

import type { GeneratedLink, InvitePrefillData } from '../types';
import { DEFAULT_EMBED_LABEL, normalizeEmbedHeight, type EmbedStyle } from '../lib/embed/options';
export type { EmbedStyle } from '../lib/embed/options';

/**
 * Unambiguous Crockford-style alphabet shared by room IDs and passcodes.
 * Excludes I, O, 0, 1 so codes stay readable and typeable. 32 symbols, a
 * power of two, which lets us reject only the unusable tail of each random
 * byte (>= 256 - 256 % 32 == 256, so no byte is ever rejected) while
 * staying bias-free via the rejection-sampling helper below.
 */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * Draw `length` characters from {@link CODE_ALPHABET} using the Web Crypto
 * CSPRNG (`crypto.getRandomValues`), available in browsers and the
 * Cloudflare Pages/worker runtime alike. Rejection sampling discards bytes
 * in the biased tail (>= the largest multiple of the alphabet size that
 * fits in a byte) so every symbol is equally likely (no modulo bias).
 *
 * @param length - Number of characters to generate.
 * @returns A random string over the unambiguous alphabet.
 */
function randomCode(length: number): string {
  const size = CODE_ALPHABET.length;
  // Largest multiple of `size` that fits in a byte. Bytes >= this are in the
  // biased tail and get rejected so the modulo below stays uniform.
  const limit = Math.floor(256 / size) * size;
  let result = '';
  // Over-allocate the random pool to cut the number of getRandomValues calls
  // while still rejecting any byte that lands in the biased tail.
  const pool = new Uint8Array(length * 2);
  while (result.length < length) {
    crypto.getRandomValues(pool);
    for (let i = 0; i < pool.length && result.length < length; i++) {
      const byte = pool[i];
      if (byte < limit) {
        result += CODE_ALPHABET.charAt(byte % size);
      }
    }
  }
  return result;
}

/**
 * Generate a unique room ID: 8 characters from the unambiguous
 * Crockford-style alphabet, drawn from the Web Crypto CSPRNG.
 */
export function generateRoomId(): string {
  return randomCode(8);
}

/**
 * Generate a passcode: 8 characters from the same unambiguous
 * Crockford-style alphabet as the room ID, drawn from the Web Crypto
 * CSPRNG. 8 symbols over a 32-char alphabet is ~1.1 trillion combinations,
 * a large jump over the legacy 6-digit numeric space, while staying
 * human-readable and typeable.
 *
 * Legacy links minted with a 6-digit numeric passcode remain valid: the
 * link payload is reversible base64 and {@link validateLinkHash} dual-accepts
 * both formats.
 */
export function generatePasscode(): string {
  return randomCode(8);
}

// Create a hash for the shareable link (base64 encoded)
export function createLinkHash(roomId: string, passcode: string): string {
  const combined = `${roomId}:${passcode}`;
  return btoa(combined);
}

// Decode a link hash to extract room_id and passcode
export function decodeLinkHash(hash: string): { roomId: string; passcode: string } | null {
  try {
    const decoded = atob(hash);
    const [roomId, passcode] = decoded.split(':');
    
    if (!roomId || !passcode) {
      return null;
    }
    
    return { roomId, passcode };
  } catch {
    return null;
  }
}

/**
 * Passcode shape accepted by the link hash and the room endpoints.
 *
 * DUAL-ACCEPT: matches BOTH the legacy 6-digit numeric passcode (already
 * issued in existing links) AND the stronger 8+ char alphanumeric passcode
 * minted for new rooms. The link payload is reversible base64 that the admin
 * UI decodes to display the cleartext passcode, so old links must keep
 * validating; only the strength inside new codes changes.
 */
const PASSCODE_FORMAT = /^(\d{6}|[A-Za-z0-9]{8,})$/;

/**
 * Validate a link hash format. The room ID must be exactly 8 characters and
 * the passcode must satisfy {@link PASSCODE_FORMAT} (legacy 6-digit OR new
 * 8+ alphanumeric). Returns false for any unparseable or malformed hash.
 */
export function validateLinkHash(hash: string): boolean {
  const decoded = decodeLinkHash(hash);
  return (
    decoded !== null &&
    decoded.roomId.length === 8 &&
    PASSCODE_FORMAT.test(decoded.passcode)
  );
}

// Generate all link components
export function generateInterviewLink(): GeneratedLink {
  const roomId = generateRoomId();
  const passcode = generatePasscode();
  const hash = createLinkHash(roomId, passcode);
  
  // Get the base URL from environment or default
  const baseUrl = typeof window !== 'undefined' 
    ? window.location.origin 
    : 'https://testimonial.holito.com';
  
  return {
    roomId,
    passcode,
    hash,
    fullUrl: `${baseUrl}/join/${hash}`,
  };
}

/** Known prefill param keys mapped to InvitePrefillData fields */
const PREFILL_KEYS = ['name', 'email', 'job', 'company', 'linkedin', 'company_url'] as const;
const META_PREFIX = 'meta_';

/**
 * Parse invite prefill data from URL search params.
 * Supports: name, email, job, company, linkedin, company_url, and meta_* for metadata.
 * @returns Parsed prefill data, or null if no recognized params are present.
 */
export function parseInviteParams(searchParams: URLSearchParams): InvitePrefillData | null {
  const result: InvitePrefillData = {};
  let hasAny = false;

  for (const key of PREFILL_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      (result as Record<string, string>)[key] = value;
      hasAny = true;
    }
  }

  const metadata: Record<string, string> = {};
  let hasMeta = false;
  searchParams.forEach((value, key) => {
    if (key.startsWith(META_PREFIX)) {
      const metaKey = key.slice(META_PREFIX.length);
      const trimmed = value.trim();
      if (metaKey && trimmed) {
        metadata[metaKey] = trimmed;
        hasMeta = true;
      }
    }
  });

  if (hasMeta) {
    result.metadata = metadata;
    hasAny = true;
  }

  return hasAny ? result : null;
}

/**
 * Build a personalized invite URL with prefill data as query params.
 * @param hash - The interview link hash (base64 encoded roomId:passcode)
 * @param prefill - Pre-fill data to include as query params
 * @returns Full invite URL with query params
 */
export function buildInviteUrl(hash: string, prefill: InvitePrefillData): string {
  const baseUrl = typeof window !== 'undefined'
    ? window.location.origin
    : 'https://testimonial.holito.com';

  const url = new URL(`/join/${hash}`, baseUrl);

  for (const key of PREFILL_KEYS) {
    const value = prefill[key];
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  if (prefill.metadata) {
    for (const [key, value] of Object.entries(prefill.metadata)) {
      if (value) {
        url.searchParams.set(`${META_PREFIX}${key}`, value);
      }
    }
  }

  return url.toString();
}

/** Options for the website embed snippet. */
export interface BuildEmbedSnippetOptions {
  /** Inline is the default for newly generated snippets. */
  style?: EmbedStyle;
  label?: string;
  title?: string;
  height?: number;
}

const HTML_ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => HTML_ENTITY_MAP[ch] ?? ch);
}

/** Build safe, portable markup while campaign behavior stays server-owned. */
export function buildEmbedSnippet(hash: string, options: BuildEmbedSnippetOptions = {}): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://interview.holito.app';
  const style = options.style ?? 'inline';
  const safeHash = escapeHtml(hash);
  const label = options.label?.trim() || DEFAULT_EMBED_LABEL;
  const titleAttr = options.title ? ` data-hollie-title="${escapeHtml(options.title)}"` : '';
  const script = `<script src="${escapeHtml(baseUrl)}/embed-v1.js" async></script>`;
  if (style === 'custom') {
    return `<button type="button" data-hollie="${safeHash}" data-hollie-style="custom"${titleAttr}>${escapeHtml(label)}</button>
${script}`;
  }
  const styleAttr = style === 'inline' ? ` data-hollie-style="inline" data-hollie-height="${normalizeEmbedHeight(options.height)}"` : '';
  const labelAttr = options.label ? ` data-hollie-label="${escapeHtml(label)}"` : '';
  return `<div data-hollie="${safeHash}"${styleAttr}${titleAttr}${labelAttr}></div>
${script}`;
}

export default {
  generateRoomId,
  generatePasscode,
  createLinkHash,
  decodeLinkHash,
  validateLinkHash,
  generateInterviewLink,
  parseInviteParams,
  buildInviteUrl,
  buildEmbedSnippet,
};
